import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Plus, Minus, Check, MapPin, Truck, Users, X, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated, isMember } = useAuth();
  
  const [step, setStep] = useState(1);
  const [snakes, setSnakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Booking form state
  const [primarySnakeId, setPrimarySnakeId] = useState('');
  const [additionalSnakeIds, setAdditionalSnakeIds] = useState([]);
  const [locationType, setLocationType] = useState('outside');
  const [locationAddress, setLocationAddress] = useState('');
  const [transportationType, setTransportationType] = useState('none');
  const [transportationAddress, setTransportationAddress] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [durationHours, setDurationHours] = useState(1);
  const [description, setDescription] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [guests, setGuests] = useState([]);
  const [newGuest, setNewGuest] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  
  // Price calculation
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  
  // Booking result
  const [booking, setBooking] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Cost split calculation
  const calculateSplit = (total, numPeople) => {
    if (numPeople <= 1) return [total];
    const totalCents = Math.round(total * 100);
    const baseCents = Math.floor(totalCents / numPeople);
    const remainder = totalCents - baseCents * numPeople;
    return Array.from({ length: numPeople }, (_, i) =>
      (baseCents + (i < remainder ? 1 : 0)) / 100
    );
  };

  // Generate available weeks starting 2 weeks from now
  const getAvailableWeeks = () => {
    const weeks = [];
    const today = new Date();
    for (let i = 2; i <= 8; i++) {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + (i * 7) - today.getDay() + 1); // Next Monday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Sunday
      weeks.push({
        value: format(weekStart, 'yyyy-MM-dd'),
        label: `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`,
        shortLabel: `Week of ${format(weekStart, 'MMM d')}`
      });
    }
    return weeks;
  };

  const availableWeeks = getAvailableWeeks();

  useEffect(() => {
    const fetchSnakes = async () => {
      try {
        const response = await fetch(`${API}/snakes`);
        const data = await response.json();
        setSnakes(data);
      } catch (error) {
        console.error('Error fetching snakes:', error);
        toast.error('Failed to load snakes');
      } finally {
        setLoading(false);
      }
    };
    fetchSnakes();
  }, []);

  useEffect(() => {
    if (primarySnakeId) {
      calculatePrice();
    }
  }, [primarySnakeId, additionalSnakeIds, locationType, transportationType, durationHours]);

  const calculatePrice = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/calculate-price`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          primary_snake_id: primarySnakeId,
          additional_snake_ids: additionalSnakeIds,
          location_type: locationType,
          transportation_type: transportationType,
          duration_hours: durationHours
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPriceBreakdown(data);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const addAdditionalSnake = (snakeId) => {
    if (!additionalSnakeIds.includes(snakeId) && snakeId !== primarySnakeId) {
      setAdditionalSnakeIds([...additionalSnakeIds, snakeId]);
    }
  };

  const removeAdditionalSnake = (snakeId) => {
    setAdditionalSnakeIds(additionalSnakeIds.filter(id => id !== snakeId));
  };

  const addGuest = () => {
    if (newGuest && !guests.includes(newGuest)) {
      setGuests([...guests, newGuest]);
      setNewGuest('');
    }
  };

  const removeGuest = (email) => {
    setGuests(guests.filter(g => g !== email));
  };

  const handleSubmitBooking = async () => {
    if (!selectedWeek) {
      toast.error('Please select a week');
      return;
    }

    // If not logged in, require name and email
    if (!isAuthenticated && (!guestName || !guestEmail)) {
      toast.error('Please provide your name and email');
      return;
    }

    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          primary_snake_id: primarySnakeId,
          additional_snake_ids: additionalSnakeIds,
          location_type: locationType,
          location_address: locationAddress,
          transportation_type: transportationType,
          transportation_address: transportationAddress,
          date: selectedWeek,
          start_time: startTime,
          duration_hours: durationHours,
          description,
          suggestion,
          guests,
          guest_name: !isAuthenticated ? guestName : undefined,
          guest_email: !isAuthenticated ? guestEmail : undefined
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      const bookingData = await response.json();
      setBooking(bookingData);
      setStep(5); // Move to payment step
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripePayment = async () => {
    if (!booking) return;
    setProcessingPayment(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          booking_id: booking.booking_id,
          origin_url: window.location.origin,
          payment_method: 'stripe'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create payment session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setProcessingPayment(false);
    }
  };

  const primarySnake = snakes.find(s => s.snake_id === primarySnakeId);
  const additionalSnakesData = snakes.filter(s => additionalSnakeIds.includes(s.snake_id));

  const steps = [
    { num: 1, label: 'Select Snake' },
    { num: 2, label: 'Location' },
    { num: 3, label: 'Date & Time' },
    { num: 4, label: 'Details' },
    { num: 5, label: 'Payment' }
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="booking-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Step Indicator */}
          <div className="flex justify-between mb-12 relative">
            <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-border -z-10" />
            {steps.map((s) => (
              <div
                key={s.num}
                className={`flex flex-col items-center ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step > s.num
                      ? 'bg-primary text-primary-foreground'
                      : step === s.num
                      ? 'bg-primary/20 border-2 border-primary text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`step-indicator-${s.num}`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className="text-xs mt-2 hidden sm:block">{s.label}</span>
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Select Snake */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Choose Your Snake</h1>
                  <p className="text-muted-foreground">Select a primary snake for your photoshoot</p>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-64 rounded-xl image-loading" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {snakes.map((snake) => (
                      <Card
                        key={snake.snake_id}
                        className={`cursor-pointer transition-all overflow-hidden ${
                          primarySnakeId === snake.snake_id
                            ? 'ring-2 ring-primary border-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setPrimarySnakeId(snake.snake_id)}
                        data-testid={`snake-select-${snake.snake_id}`}
                      >
                        <div className="relative h-48">
                          <img
                            src={snake.image_url}
                            alt={snake.name}
                            className="w-full h-full object-cover"
                          />
                          {primarySnakeId === snake.snake_id && (
                            <div className="absolute top-2 right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                              <Check className="w-5 h-5 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold">{snake.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {snake.length_ft}ft • {snake.color}
                              </p>
                            </div>
                            <span className="price-tag px-2 py-1 rounded text-sm">
                              ${snake.hourly_rate}/hr
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Additional Snakes */}
                {primarySnakeId && (
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold mb-4">Add More Snakes (under 5ft +$5/hr, 5ft+ +$10/hr)</h3>
                    <div className="flex flex-wrap gap-2">
                      {snakes
                        .filter(s => s.snake_id !== primarySnakeId)
                        .map((snake) => (
                          <Button
                            key={snake.snake_id}
                            variant={additionalSnakeIds.includes(snake.snake_id) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() =>
                              additionalSnakeIds.includes(snake.snake_id)
                                ? removeAdditionalSnake(snake.snake_id)
                                : addAdditionalSnake(snake.snake_id)
                            }
                            className={additionalSnakeIds.includes(snake.snake_id) ? 'bg-primary' : ''}
                            data-testid={`add-snake-${snake.snake_id}`}
                          >
                            {additionalSnakeIds.includes(snake.snake_id) ? (
                              <Minus className="w-4 h-4 mr-1" />
                            ) : (
                              <Plus className="w-4 h-4 mr-1" />
                            )}
                            {snake.name}
                          </Button>
                        ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!primarySnakeId}
                    className="btn-primary"
                    data-testid="step1-next-btn"
                  >
                    Next: Location
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Location */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Choose Location</h1>
                  <p className="text-muted-foreground">Where would you like your photoshoot?</p>
                </div>

                <div className="space-y-6">
                  <RadioGroup value={locationType} onValueChange={setLocationType}>
                    <div className="grid md:grid-cols-3 gap-4">
                      <label className="cursor-pointer">
                        <Card
                          className={`p-6 text-center transition-all ${
                            locationType === 'studio' ? 'ring-2 ring-primary border-primary' : 'border-border'
                          }`}
                          data-testid="location-studio"
                        >
                          <RadioGroupItem value="studio" className="sr-only" />
                          <MapPin className="w-8 h-8 mx-auto mb-3 text-primary" />
                          <h3 className="font-semibold mb-1">Studio Rental</h3>
                          <p className="text-sm text-muted-foreground mb-2">Professional studio space</p>
                          <span className="text-primary font-semibold">+$60/hr</span>
                        </Card>
                      </label>
                      <label className="cursor-pointer">
                        <Card
                          className={`p-6 text-center transition-all ${
                            locationType === 'outside' ? 'ring-2 ring-primary border-primary' : 'border-border'
                          }`}
                          data-testid="location-outside"
                        >
                          <RadioGroupItem value="outside" className="sr-only" />
                          <MapPin className="w-8 h-8 mx-auto mb-3 text-primary" />
                          <h3 className="font-semibold mb-1">Outside Shoot</h3>
                          <p className="text-sm text-muted-foreground mb-2">Outdoor location of choice</p>
                          <span className="text-primary font-semibold">No extra fee</span>
                        </Card>
                      </label>
                      <label className="cursor-pointer">
                        <Card
                          className={`p-6 text-center transition-all ${
                            locationType === 'own_location' ? 'ring-2 ring-primary border-primary' : 'border-border'
                          }`}
                          data-testid="location-own"
                        >
                          <RadioGroupItem value="own_location" className="sr-only" />
                          <MapPin className="w-8 h-8 mx-auto mb-3 text-primary" />
                          <h3 className="font-semibold mb-1">Your Location</h3>
                          <p className="text-sm text-muted-foreground mb-2">Host at your own space</p>
                          <span className="text-primary font-semibold">No extra fee</span>
                        </Card>
                      </label>
                    </div>
                  </RadioGroup>

                  {(locationType === 'outside' || locationType === 'own_location') && (
                    <div>
                      <Label htmlFor="locationAddress">Location Address</Label>
                      <Input
                        id="locationAddress"
                        value={locationAddress}
                        onChange={(e) => setLocationAddress(e.target.value)}
                        placeholder="Enter the address for your shoot"
                        className="mt-2"
                        data-testid="location-address-input"
                      />
                    </div>
                  )}

                  {/* Transportation */}
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                      <Truck className="w-5 h-5 mr-2 text-primary" />
                      Transportation Options
                    </h3>
                    <RadioGroup value={transportationType} onValueChange={setTransportationType}>
                      <div className="grid md:grid-cols-2 gap-4">
                        <label className="cursor-pointer">
                          <Card
                            className={`p-4 transition-all ${
                              transportationType === 'none' ? 'ring-2 ring-primary border-primary' : 'border-border'
                            }`}
                            data-testid="transport-none"
                          >
                            <RadioGroupItem value="none" className="sr-only" />
                            <h4 className="font-medium">No Transportation Needed</h4>
                            <p className="text-sm text-muted-foreground">I'll arrange my own transport</p>
                          </Card>
                        </label>
                        <label className="cursor-pointer">
                          <Card
                            className={`p-4 transition-all ${
                              transportationType === 'pickup' ? 'ring-2 ring-primary border-primary' : 'border-border'
                            }`}
                            data-testid="transport-pickup"
                          >
                            <RadioGroupItem value="pickup" className="sr-only" />
                            <div className="flex justify-between">
                              <div>
                                <h4 className="font-medium">Pick Up Only</h4>
                                <p className="text-sm text-muted-foreground">We'll pick you up</p>
                              </div>
                              <span className="text-primary font-semibold">+$25</span>
                            </div>
                          </Card>
                        </label>
                        <label className="cursor-pointer">
                          <Card
                            className={`p-4 transition-all ${
                              transportationType === 'dropoff' ? 'ring-2 ring-primary border-primary' : 'border-border'
                            }`}
                            data-testid="transport-dropoff"
                          >
                            <RadioGroupItem value="dropoff" className="sr-only" />
                            <div className="flex justify-between">
                              <div>
                                <h4 className="font-medium">Drop Off Only</h4>
                                <p className="text-sm text-muted-foreground">We'll drop you off after</p>
                              </div>
                              <span className="text-primary font-semibold">+$25</span>
                            </div>
                          </Card>
                        </label>
                        <label className="cursor-pointer">
                          <Card
                            className={`p-4 transition-all ${
                              transportationType === 'both' ? 'ring-2 ring-primary border-primary' : 'border-border'
                            }`}
                            data-testid="transport-both"
                          >
                            <RadioGroupItem value="both" className="sr-only" />
                            <div className="flex justify-between">
                              <div>
                                <h4 className="font-medium">Pick Up & Drop Off</h4>
                                <p className="text-sm text-muted-foreground">Full transportation service</p>
                              </div>
                              <span className="text-primary font-semibold">+$50</span>
                            </div>
                          </Card>
                        </label>
                      </div>
                    </RadioGroup>

                    {transportationType !== 'none' && (
                      <div className="mt-4">
                        <Label htmlFor="transportAddress">Transportation Address</Label>
                        <Input
                          id="transportAddress"
                          value={transportationAddress}
                          onChange={(e) => setTransportationAddress(e.target.value)}
                          placeholder="Enter pickup/dropoff address"
                          className="mt-2"
                          data-testid="transport-address-input"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back-btn">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button onClick={() => setStep(3)} className="btn-primary" data-testid="step2-next-btn">
                    Next: Date & Time
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Week & Time */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Select Your Week</h1>
                  <p className="text-muted-foreground">Choose which week works best for your session (earliest availability: 2 weeks out)</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <Label className="text-lg mb-4 block">Available Weeks</Label>
                    <div className="space-y-3" data-testid="week-selector">
                      {availableWeeks.map((week) => (
                        <label key={week.value} className="cursor-pointer block">
                          <Card
                            className={`p-4 transition-all ${
                              selectedWeek === week.value ? 'ring-2 ring-primary border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                            }`}
                            data-testid={`week-option-${week.value}`}
                            onClick={() => setSelectedWeek(week.value)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{week.shortLabel}</p>
                                <p className="text-sm text-muted-foreground">{week.label}</p>
                              </div>
                              {selectedWeek === week.value && (
                                <Check className="w-5 h-5 text-primary" />
                              )}
                            </div>
                          </Card>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label className="text-lg mb-4 block">Preferred Time</Label>
                      <Select value={startTime} onValueChange={setStartTime}>
                        <SelectTrigger data-testid="time-select">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-lg mb-4 block">Duration (Hours)</Label>
                      <div className="flex items-center space-x-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setDurationHours(Math.max(1, durationHours - 1))}
                          data-testid="duration-decrease"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-2xl font-semibold w-16 text-center" data-testid="duration-value">
                          {durationHours}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setDurationHours(durationHours + 1)}
                          data-testid="duration-increase"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {selectedWeek && (
                      <Card className="p-4 bg-primary/10 border-primary/30">
                        <p className="text-sm text-muted-foreground">Selected:</p>
                        <p className="font-semibold">
                          {availableWeeks.find(w => w.value === selectedWeek)?.label} at {startTime}
                        </p>
                        <p className="text-sm text-primary">Duration: {durationHours} hour(s)</p>
                      </Card>
                    )}

                    {/* Suggestion Box */}
                    <div>
                      <Label htmlFor="suggestion">Suggestions or Special Requests</Label>
                      <Textarea
                        id="suggestion"
                        value={suggestion}
                        onChange={(e) => setSuggestion(e.target.value)}
                        placeholder="Have a preferred day within the week? Any scheduling notes or suggestions..."
                        className="mt-2 min-h-[100px]"
                        data-testid="suggestion-textarea"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="step3-back-btn">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(4)}
                    disabled={!selectedWeek}
                    className="btn-primary"
                    data-testid="step3-next-btn"
                  >
                    Next: Details
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Details */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Additional Details</h1>
                  <p className="text-muted-foreground">Tell us more about your shoot</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="description">Theme / Additional Information</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe your vision, theme, or any special requests..."
                      className="mt-2 min-h-[120px]"
                      data-testid="description-textarea"
                    />
                  </div>

                  {/* Guest Splitting (Members Only) */}
                  {isMember ? (
                    <div>
                      <Label className="flex items-center mb-4">
                        <Users className="w-5 h-5 mr-2 text-primary" />
                        Add Guests & Split Costs
                        <span className="member-badge ml-2">Member Perk</span>
                      </Label>
                      <div className="flex gap-2 mb-4">
                        <Input
                          type="email"
                          value={newGuest}
                          onChange={(e) => setNewGuest(e.target.value)}
                          placeholder="Guest email address"
                          data-testid="guest-email-input"
                        />
                        <Button onClick={addGuest} variant="outline" data-testid="add-guest-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {guests.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {guests.map((guest) => (
                            <span
                              key={guest}
                              className="bg-muted px-3 py-1 rounded-full text-sm flex items-center"
                            >
                              {guest}
                              <button
                                onClick={() => removeGuest(guest)}
                                className="ml-2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {guests.length > 0 && priceBreakdown && (
                        <div className="mt-4 p-4 bg-primary/10 rounded-lg border border-primary/30" data-testid="cost-split-preview">
                          <p className="text-sm font-semibold mb-2 flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Cost Split ({guests.length + 1} people):
                          </p>
                          <div className="space-y-1">
                            {calculateSplit(priceBreakdown.total, guests.length + 1).map((amount, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {i === 0 ? `You (${user?.name || 'Member'})` : guests[i - 1]}
                                </span>
                                <span className="font-medium text-primary">${amount.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : !isAuthenticated ? (
                    <div className="space-y-4" data-testid="guest-info-section">
                      <Label className="text-lg font-semibold block">Your Contact Info</Label>
                      <p className="text-sm text-muted-foreground -mt-2">Required to confirm your booking</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="guestName">Your Name</Label>
                          <Input
                            id="guestName"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            placeholder="Full name"
                            className="mt-1"
                            data-testid="guest-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="guestEmail">Your Email</Label>
                          <Input
                            id="guestEmail"
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            placeholder="email@example.com"
                            className="mt-1"
                            data-testid="guest-email-field"
                          />
                        </div>
                      </div>
                      <Card className="p-3 bg-muted/50 border-border">
                        <p className="text-xs text-muted-foreground">
                          <Users className="w-3 h-3 inline mr-1" />
                          Want to add guests and split costs? <Button variant="link" className="text-primary p-0 h-auto text-xs" onClick={() => navigate('/register')}>Sign up</Button> to unlock member perks (free, automatic membership).
                        </p>
                      </Card>
                    </div>
                  ) : null}

                  {/* Price Summary */}
                  {priceBreakdown && (
                    <Card className="p-6 bg-card border-border">
                      <h3 className="text-xl font-semibold mb-4">Price Summary</h3>
                      <div className="space-y-2">
                        <div className="pricing-row">
                          <span>Base Rate ({primarySnake?.name})</span>
                          <span>${priceBreakdown.breakdown.snake_base_rate}/hr × {durationHours}hr</span>
                        </div>
                        {additionalSnakeIds.length > 0 && (
                          <div className="pricing-row">
                            <span>Additional Snakes ({additionalSnakeIds.length})</span>
                            <span>${priceBreakdown.breakdown.additional_snakes_rate}/hr × {durationHours}hr</span>
                          </div>
                        )}
                        {locationType === 'studio' && (
                          <div className="pricing-row">
                            <span>Studio Rental</span>
                            <span>${priceBreakdown.breakdown.studio_rate}/hr × {durationHours}hr</span>
                          </div>
                        )}
                        {transportationType !== 'none' && (
                          <div className="pricing-row">
                            <span>Transportation</span>
                            <span>${priceBreakdown.breakdown.transport_rate}</span>
                          </div>
                        )}
                        <div className="pricing-row font-medium">
                          <span>Subtotal</span>
                          <span>${priceBreakdown.subtotal.toFixed(2)}</span>
                        </div>
                        {priceBreakdown.discount > 0 && (
                          <div className="pricing-row text-secondary">
                            <span className="flex items-center">
                              Member Discount (20%)
                              <span className="discount-badge ml-2">First Shoot</span>
                            </span>
                            <span>-${priceBreakdown.discount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="pt-4 mt-4 border-t border-border">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">Total</span>
                            <span className="pricing-total">${priceBreakdown.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(3)} data-testid="step4-back-btn">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmitBooking}
                    disabled={submitting || (!isAuthenticated && (!guestName || !guestEmail))}
                    className="btn-primary"
                    data-testid="step4-submit-btn"
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <div className="spinner mr-2" />
                        Creating Booking...
                      </span>
                    ) : (
                      <>
                        Continue to Payment
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Payment */}
            {step === 5 && booking && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Complete Payment</h1>
                  <p className="text-muted-foreground">Pay securely with card, Google Pay, or Apple Pay</p>
                </div>

                <Card className="p-6 bg-card border-border max-w-lg mx-auto" data-testid="payment-card">
                  <div className="text-center mb-6">
                    <p className="text-muted-foreground">Total Amount</p>
                    <p className="text-4xl font-bold text-primary" data-testid="payment-total">
                      ${booking.total_price.toFixed(2)}
                    </p>
                    {booking.discount_applied > 0 && (
                      <p className="text-sm text-secondary">
                        You saved ${booking.discount_applied.toFixed(2)} with member discount!
                      </p>
                    )}
                  </div>

                  {/* Cost Split Breakdown */}
                  {booking.guests && booking.guests.length > 0 && (
                    <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/30" data-testid="payment-cost-split">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Cost Split ({booking.guests.length + 1} people)
                      </h4>
                      <div className="space-y-2">
                        {calculateSplit(booking.total_price, booking.guests.length + 1).map((amount, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span>{i === 0 ? `You (${user?.name || 'Member'})` : booking.guests[i - 1]}</span>
                            <span className="font-medium">${amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 border-t border-primary/20 pt-2">
                        Full payment is collected now. Share the split details with your guests.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <Button
                      onClick={handleStripePayment}
                      disabled={processingPayment}
                      className="w-full btn-primary h-14 text-lg"
                      data-testid="stripe-pay-btn"
                    >
                      {processingPayment ? (
                        <span className="flex items-center">
                          <div className="spinner mr-2" />
                          Redirecting to Payment...
                        </span>
                      ) : (
                        <>
                          <CreditCard className="mr-2 w-5 h-5" />
                          Pay ${booking.total_price.toFixed(2)}
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-3 text-muted-foreground text-xs pt-2">
                      <span>Card</span>
                      <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                      <span>Google Pay</span>
                      <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                      <span>Apple Pay</span>
                    </div>

                    <p className="text-xs text-center text-muted-foreground">
                      Secure payment powered by Stripe
                    </p>
                  </div>
                </Card>

                <div className="text-center">
                  <Button variant="ghost" onClick={() => setStep(4)} data-testid="step5-back-btn">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back to Details
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
